import {
  ChangeEvent,
  KeyboardEvent,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  ChakraButton,
  ChakraGridItem,
  ChakraSimpleGrid,
  ChakraText,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Send,
  TextInput,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import { ActivityField } from '~/lib/constants/task.constants';
import {
  TaskUserTypeNameMAPPER,
  UNASSIGNED_PATIENT,
} from '~/lib/constants/user.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { getPreferredFullName } from '~/lib/util/patient.util';
import {
  CompServTask,
  TaskStatusLabel,
  getAssigneeNameOrUnassigned,
  getChangedActivityFields,
  taskStatusDefinitions,
} from '~/lib/util/task.util';
import { getDisplayNameOrLegalNameForProvider } from '~/lib/util/user.util';
import { dateStringToUTC } from '~/lib/utils';

import ActivityLogEntry, {
  LogEntry,
  ParsedActivityEvent,
} from './ActivityLogEntry';
import {
  useCreateCommentMutation,
  useLazyGetTaskActivityByTaskIdQuery,
  useLazyGetTaskCommentsByTaskIdQuery,
} from '~/store/services/task';

const { neutral } = EquipUIFireflyV2Theme.colors;
const { caption, labelBold } = EquipUITheme.typography;

const compareDates = (a: string | undefined, b: string | undefined): number => {
  if (!b) return -1;
  if (!a) return 1;
  return dateStringToUTC(a).getTime() - dateStringToUTC(b).getTime();
};

type CombinedEvent = {
  date?: string;
  key: string;
  node: ReactElement;
};

type ActivitySectionProps = {
  isNonEditable: boolean;
  taskData: CompServTask;
  taskExternalId: string;
  taskEditCounter: number;
};

/* eslint-disable no-param-reassign */
const taskStatusTitleValueMapper = taskStatusDefinitions.reduce(
  (map, obj): Record<string, string> => {
    map[obj.title] = obj.value;
    return map;
  },
  {},
);
/* eslint-enable no-param-reassign */

const getUpdatedBy = ({
  activity,
  field,
  taskData,
}: {
  activity: TaskActivityEvent;
  field: ActivityField;
  taskData: CompServTask;
}): string => {
  const { completedByFamilyUser, isFamilyTask } = taskData ?? {};
  if (
    field === ActivityField.STATUS_ID &&
    isFamilyTask &&
    completedByFamilyUser &&
    String(activity[field]) === taskStatusTitleValueMapper[TaskStatusLabel.DONE]
  ) {
    const { chosenName, firstName, lastName, userType } = completedByFamilyUser;
    return `${getPreferredFullName(chosenName, firstName, lastName)} (${
      TaskUserTypeNameMAPPER[userType]
    })`;
  }

  return getDisplayNameOrLegalNameForProvider(
    activity.updatedByUserDisplayFirstName,
    activity.updatedByUserFirstName,
    activity.updatedByUserDisplayLastName,
    activity.updatedByUserLastName,
  );
};

const ActivitySection = ({
  isNonEditable,
  taskData,
  taskExternalId,
  taskEditCounter,
}: ActivitySectionProps) => {
  const [comment, setComment] = useState<string>('');
  const [newCommentPlaceholder, setNewCommentPlaceholder] =
    useState<TaskComment>(null);
  const [sortedTaskComments, setSortedTaskComments] = useState<TaskComment[]>(
    [],
  );
  const [activityLog, setActivityLog] = useState<ParsedActivityEvent[]>([]);
  const [combinedEvents, setCombinedEvents] = useState<CombinedEvent[]>([]);

  const { currentUser } = useGlobalContext();

  const [
    getCommentsByTaskId,
    {
      data: getCommentsByTaskIdData,
      isLoading: isCommentsLoading,
      isError: isCommentsError,
      error: getCommentsError,
    },
  ] = useLazyGetTaskCommentsByTaskIdQuery();
  const commentsData = getCommentsByTaskIdData && [...getCommentsByTaskIdData];

  const [
    getActivityByTaskId,
    {
      data: rawActivityData,
      isLoading: isActivityLoading,
      isError: isActivityError,
      error: getActivityError,
    },
  ] = useLazyGetTaskActivityByTaskIdQuery();

  useEffect(() => {
    if (taskExternalId) {
      getCommentsByTaskId({ taskExternalId });
      getActivityByTaskId({ taskExternalId });
    }
  }, [taskExternalId]);

  const timer = useRef(null);

  // delay activity fetch to give server time to log the activity after a task is edited
  useEffect(() => {
    if (taskEditCounter > 0) {
      if (timer?.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        getActivityByTaskId({ taskExternalId });
      }, 500);
    }
    return () => {
      if (timer?.current) clearTimeout(timer.current);
    };
  }, [taskEditCounter]);

  useEffect(() => {
    /** @TODO sorting should be handled by the backend as part of the SQL query */
    setSortedTaskComments(
      (commentsData ?? []).sort(
        (a, b) =>
          new Date(b.commentCreatedDate).getTime() -
          new Date(a.commentCreatedDate).getTime(),
      ),
    );
  }, [commentsData?.length]);

  // parse activity data
  useEffect(() => {
    if (!rawActivityData || !rawActivityData.length) return;
    // we need the data sorted with most recent activity first
    const raw = rawActivityData.slice().reverse();
    const changes: ParsedActivityEvent[] = [];
    raw
      .map((activity: TaskActivityEvent) => ({
        ...activity,
        // make sure we include the entry when a patient has been unlinked
        patientName: activity.patientName ?? UNASSIGNED_PATIENT.firstName,
      }))
      .forEach(
        (
          activity: TaskActivityEvent,
          i: number,
          activities: TaskActivityEvent[],
        ) => {
          if (i === activities.length - 1) return; // handle creation separately
          const prevActivity = activities[i + 1];
          const changedFields = getChangedActivityFields(
            activity,
            prevActivity,
          );
          changedFields.forEach((field: ActivityField) => {
            const isAssigneeField = field === ActivityField.ASSIGNEE_NAME;

            const newValueDisplayOrLegalName = isAssigneeField
              ? getAssigneeNameOrUnassigned(activity)
              : '';
            const prevValueDisplayOrLegalName = isAssigneeField
              ? getAssigneeNameOrUnassigned(prevActivity)
              : '';

            changes.push({
              activityExternalId: activity.activityExternalId,
              fieldChanged: field,
              newValue: isAssigneeField
                ? newValueDisplayOrLegalName
                : activity[field],
              previousValue: isAssigneeField
                ? prevValueDisplayOrLegalName
                : prevActivity[field],
              statusId: activity.statusId?.toString(),
              updatedAt: activity.updatedDate,
              updatedBy: getUpdatedBy({
                activity,
                field,
                taskData,
              }),
              voidReason: activity.voidReason,
            });
          });
        },
      );

    // deal with creation event

    const creationEvent = rawActivityData[0];
    const updatedByDisplayOrLegalName = getDisplayNameOrLegalNameForProvider(
      creationEvent.updatedByUserDisplayFirstName,
      creationEvent.updatedByUserFirstName,
      creationEvent.updatedByUserDisplayLastName,
      creationEvent.updatedByUserLastName,
    );
    changes.push({
      activityExternalId: creationEvent.activityExternalId,
      fieldChanged: ActivityField.CREATED,
      newValue: '',
      updatedAt: creationEvent.updatedDate,
      updatedBy: updatedByDisplayOrLegalName,
    });
    setActivityLog(changes);
  }, [rawActivityData?.length]);

  const [
    createComment,
    { isError: isCreateCommentError, isLoading: isCreateCommentLoading },
  ] = useCreateCommentMutation();

  const handleTaskCommentAdd = (value: string): void => {
    const { firstName, lastName, userId } = currentUser;
    const baseOptions: TaskComment = {
      commentContent: value,
      commentCreatedByUserId: userId,
      commentCreatedByUserName: `${firstName} ${lastName}`,
      taskExternalId,
    };

    const now = DateTime.now();

    const options: TaskCommentPostRequest = {
      ...baseOptions,
      commentCreatedDateTime: now.toFormat('yyyy-LL-dd TT'),
      timeZoneId: now.toFormat('z'),
    };

    setNewCommentPlaceholder({
      ...baseOptions,
      commentCreatedDate: new Date().toISOString(),
    });

    createComment(options)
      .unwrap()
      .then((res) => {
        const commentExternalId = res?.commentExternalId;
        if (commentExternalId) {
          setSortedTaskComments([
            { ...newCommentPlaceholder, commentExternalId },
            ...(sortedTaskComments ?? []),
          ]);
        }
        setComment('');
        setNewCommentPlaceholder(null);
      })
      .catch((e) => console.error('failed to create comment', e));
  };

  // combine comments and activity, sorted by date
  useEffect(() => {
    const events: CombinedEvent[] = [
      ...activityLog.map((activity) => ({
        date: activity.updatedAt,
        key: `${activity.activityExternalId}::${activity.fieldChanged}`,
        node: <ActivityLogEntry {...activity} />,
      })),
      ...sortedTaskComments.map((comment) => {
        const createdByUserName = getDisplayNameOrLegalNameForProvider(
          comment?.createdByUser?.displayFirstName,
          comment?.createdByUser?.firstName,
          comment?.createdByUser?.displayLastName,
          comment?.createdByUser?.lastName,
        );
        const avatarUrl =
          comment?.createdByUser?.avatarThumbnailUrl ||
          comment?.createdByUser?.avatarUrl;
        return {
          date: comment.commentCreatedDate,
          key: comment.commentExternalId,
          node: (
            <LogEntry
              avatarUrl={avatarUrl}
              commentedUserExternalId={comment.commentCreatedByUserId}
              secondaryText={comment.commentContent}
              subtitle={DateTime.fromJSDate(
                new Date(comment.commentCreatedDate),
              ).toFormat('LL/dd/yyyy h:mm a')}
              title={createdByUserName}
            />
          ),
        };
      }),
    ];
    events.sort((a, b) => compareDates(b.date, a.date));
    setCombinedEvents(events);
  }, [sortedTaskComments.length, activityLog.length]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    setComment(e.target.value);
  };

  const handleSend = () => {
    if (comment.trim()) {
      handleTaskCommentAdd(comment);
    }
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (comment.trim() && e.key === 'Enter') {
      handleTaskCommentAdd(comment);
    }
  };

  const isLoading =
    isCommentsLoading || isActivityLoading || isCreateCommentLoading;

  return (
    <ChakraGridItem borderTop={`2px solid ${neutral.line}`} padding="16px 0">
      <ChakraText color="neutral.secondary" marginBottom="16px" {...labelBold}>
        Activity
      </ChakraText>
      {isCommentsError && getCommentsError && (
        <ChakraText color="critical.base" marginBottom="16px" {...caption}>
          Error loading comments
        </ChakraText>
      )}
      {isActivityError && getActivityError && (
        <ChakraText color="critical.base" marginBottom="16px" {...caption}>
          Error loading activity
        </ChakraText>
      )}
      <>
        {!isNonEditable && (
          <TextInput
            as="paragraph"
            endIcon={
              <ChakraButton
                borderRadius="12px"
                disabled={isLoading}
                marginRight="8px"
                onClick={handleSend}
                padding="0px"
                variant="ghost"
              >
                <Send />
              </ChakraButton>
            }
            endIconPointerEvents="auto"
            errorMessage={isCreateCommentError && 'Error saving comment'}
            isDisabled={isLoading}
            isInvalid={isCreateCommentError}
            label="Leave a comment"
            margin={0}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Write your comment here..."
            value={comment}
          />
        )}
        <ChakraSimpleGrid flexDirection="column" gap={4} marginTop="32px">
          {combinedEvents.map(({ key, node }) => (
            <ChakraGridItem key={key}>{node}</ChakraGridItem>
          ))}
        </ChakraSimpleGrid>
      </>
    </ChakraGridItem>
  );
};

export default ActivitySection;
